import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { MEMBER_PASSWORD_RULE_LABEL, MEMBER_PASSWORD_RULE_ERROR, validatePassword } from '../utils/memberUtils';
import { AUTH_LIGHT_SCREEN_BG, useBodyBackgroundOnMount } from '../hooks/useBodyBackgroundOnMount';

export default function ResetPassword() {
  useBodyBackgroundOnMount(AUTH_LIGHT_SCREEN_BG);

  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [hasRecoverySession, setHasRecoverySession] = useState<boolean | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const formatResetErrorJa = (msg: string): string => {
    if (msg.includes('should be different from the old password')) {
      return '新しいパスワードは現在のパスワードと異なるものを設定してください。';
    }
    if (msg.includes('at least')) {
      return MEMBER_PASSWORD_RULE_ERROR;
    }
    return msg || 'パスワードのリセットに失敗しました。';
  };

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        // In Supabase "recovery" flow, the session is set from the URL automatically
        // because `detectSessionInUrl: true` is enabled in `supabaseClient`.
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        if (cancelled) return;
        setHasRecoverySession(!!data.session);
      } catch (e) {
        if (cancelled) return;
        setHasRecoverySession(false);
      }
    };

    check();
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      setHasRecoverySession(!!session);
    });

    return () => {
      cancelled = true;
      try {
        data.subscription.unsubscribe();
      } catch {
        // noop
      }
    };
  }, []);

  // NOTE:
  // When the user opens the Supabase recovery link, supabase-js will set a session from the URL.
  // If this doesn't happen, the `hasRecoverySession === false` branch renders a clear inline message.

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    
    if (!hasRecoverySession) {
      setFormError('無効なリセットリンクです。');
      return;
    }

    // バリデーション
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.valid) {
      setFormError(passwordValidation.error ?? 'パスワードが無効です。');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setFormError('パスワードが一致しません。');
      return;
    }

    setLoading(true);
    try {
      // Demo account policy: do not allow password reset for the shared demo user.
      const { data: userData } = await supabase.auth.getUser();
      const email = String(userData.user?.email ?? '').trim().toLowerCase();
      if (email === 'demo@handra.jp') {
        setFormError('デモユーザーはパスワード再設定できません。');
        try {
          await supabase.auth.signOut();
        } catch {}
        navigate('/login');
        return;
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
        data: {
          requires_password_setup: false,
          personal_login_setup_completed: true,
        },
      });
      if (error) throw error;

      // Keep behavior consistent: after reset, go back to login screen.
      try {
        await supabase.auth.signOut();
      } catch {}

      setSuccess(true);
      
      // 3秒後にログインページにリダイレクト
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error: any) {
      console.error('Password reset error:', error);
      setFormError(formatResetErrorJa(String(error?.message ?? '')));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-teal-600">
              Handra
            </CardTitle>
            <CardDescription>
              パスワードリセット完了
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-center">
              <p className="text-gray-700">
                パスワードをリセットしました。
              </p>
              <p className="text-sm text-gray-500">
                ログインページにリダイレクトします...
              </p>
              <div className="pt-4">
                <Link to="/login">
                  <Button variant="cta" className="w-full">
                    ログインページへ
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (hasRecoverySession === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-teal-600">
              Handra
            </CardTitle>
            <CardDescription>
              パスワードリセット
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-center">
              <p className="text-gray-700">
                リセットリンクが無効、または期限切れです。
              </p>
              <p className="text-sm text-gray-500">
                <strong>メールに記載されたリンク</strong>を開いてください（URLをコピーして直接開いた場合などは失敗することがあります）。
              </p>
              <div className="pt-4">
                <Link to="/forgot-password">
                  <Button variant="cta" className="w-full">
                    リセットメールを再送する
                  </Button>
                </Link>
              </div>
              <div className="text-center text-sm">
                <Link to="/login" className="text-teal-600 hover:underline">
                  ログインページに戻る
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (hasRecoverySession === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-teal-600">
              Handra
            </CardTitle>
            <CardDescription>
              パスワードリセット
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-gray-500">
              認証情報を確認中です...
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-teal-600">
            Handra
          </CardTitle>
          <CardDescription>
            新しいパスワードを設定
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">新しいパスワード（{MEMBER_PASSWORD_RULE_LABEL}）</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="new-password"
                  type="password"
                  placeholder="新しいパスワードを入力"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10 placeholder:text-gray-300"
                  required
                  disabled={loading}
                />
              </div>
              <p className="text-xs text-gray-500">
                {MEMBER_PASSWORD_RULE_LABEL}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm-password">パスワード（確認）</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="パスワードを再入力"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 placeholder:text-gray-300"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            
            {formError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {formError}
              </p>
            )}

            <div className="pt-2">
              <Button
                type="submit"
                variant="cta"
                className="w-full"
                disabled={loading || !hasRecoverySession}
              >
                {loading ? 'リセット中...' : 'パスワードをリセット'}
              </Button>
            </div>
            
            <div className="text-center text-sm">
              <Link to="/login" className="text-teal-600 hover:underline">
                ログインページに戻る
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}




